import React from 'react';

/**
 * The single-card SVG drawing shared by the palette preview
 * ({@link ImageCardPreviewGraphic}) and the editor's "add a slide" placeholder.
 * A rounded card with a title bar, an image area holding an apartment-building
 * skyline glyph, and a few prose lines.
 */

export const CARD_W = 353;
export const CARD_H = 344;
const PAD = 22;
const IMG_H = 176;
const INNER_W = CARD_W - PAD * 2;
const IMG_Y = PAD + 34;

function windowGrid(
  bx: number,
  by: number,
  cols: number,
  rows: number,
  stepX: number,
  stepY: number,
): Array<{ x: number; y: number }> {
  const cells: Array<{ x: number; y: number }> = [];
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      cells.push({ x: bx + c * stepX, y: by + r * stepY });
    }
  }
  return cells;
}

/** A simple apartment-building skyline, drawn to sit on `bottom` within the image area. */
function ApartmentGlyph({ x0, bottom }: { x0: number; bottom: number }) {
  const back = { x: x0 + 48, w: 40, top: bottom - 124, fill: '#B7C0C7' };
  const main = { x: x0 + 86, w: 112, top: bottom - 152, fill: '#C2CAD0' };
  const side = { x: x0 + 206, w: 58, top: bottom - 104, fill: '#BCC5CB' };
  return (
    <>
      <rect x={back.x} y={back.top} width={back.w} height={bottom - back.top} fill={back.fill} />
      {windowGrid(back.x + 9, back.top + 12, 2, 5, 18, 23).map((p, i) => (
        <rect key={`b${i}`} x={p.x} y={p.y} width="10" height="11" rx="2" fill="#DCE0E2" />
      ))}
      <rect x={main.x} y={main.top} width={main.w} height={bottom - main.top} fill={main.fill} />
      {windowGrid(main.x + 14, main.top + 16, 4, 5, 26, 28).map((p, i) => (
        <rect key={`m${i}`} x={p.x} y={p.y} width="16" height="14" rx="2" fill="#DCE0E2" />
      ))}
      <rect x={side.x} y={side.top} width={side.w} height={bottom - side.top} fill={side.fill} />
      {windowGrid(side.x + 12, side.top + 14, 2, 4, 22, 24).map((p, i) => (
        <rect key={`s${i}`} x={p.x} y={p.y} width="12" height="12" rx="2" fill="#DCE0E2" />
      ))}
    </>
  );
}

interface ImageCardBodyProps {
  /** Unique id for this card's image clip path. */
  clipId: string;
  title: string;
  /** Translate offset when composing several cards in one SVG. */
  x?: number;
  y?: number;
}

/** One card's contents, offset by (x, y). Must be rendered inside an `<svg>`. */
export function ImageCardBody({ clipId, title, x = 0, y = 0 }: ImageCardBodyProps) {
  return (
    <g transform={`translate(${x}, ${y})`}>
      <rect width={CARD_W} height={CARD_H} rx="20" fill="#F1F2EF" stroke="#E4E4E0" />

      <text
        x={PAD + 2}
        y={PAD + 18}
        fontFamily="Inter, system-ui, -apple-system, sans-serif"
        fontSize="18"
        fontWeight="700"
        fill="#1D1D1D"
      >
        {title}
      </text>

      <defs>
        <clipPath id={clipId}>
          <rect x={PAD} y={IMG_Y} width={INNER_W} height={IMG_H} rx="8" />
        </clipPath>
      </defs>
      <rect x={PAD} y={IMG_Y} width={INNER_W} height={IMG_H} rx="8" fill="#DCE0E2" />
      <g clipPath={`url(#${clipId})`}>
        <ApartmentGlyph x0={PAD} bottom={IMG_Y + IMG_H} />
      </g>

      <rect x={PAD} y={IMG_Y + IMG_H + 24} width={INNER_W} height="11" rx="5.5" fill="#C7CBD0" />
      <rect
        x={PAD}
        y={IMG_Y + IMG_H + 46}
        width={INNER_W * 0.82}
        height="11"
        rx="5.5"
        fill="#C7CBD0"
      />
      <rect
        x={PAD}
        y={IMG_Y + IMG_H + 68}
        width={INNER_W * 0.48}
        height="11"
        rx="5.5"
        fill="#D8DBDE"
      />
    </g>
  );
}

/** Standalone one-card illustration that scales with its container. */
export default function ImageCardGlyph({ title = 'Image card' }: { title?: string }) {
  const clipId = React.useId();
  return (
    <svg
      viewBox={`0 0 ${CARD_W} ${CARD_H}`}
      width="100%"
      role="img"
      aria-label="Image card placeholder"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block' }}
    >
      <ImageCardBody clipId={clipId} title={title} />
    </svg>
  );
}
